import React, { useEffect, useMemo, useState, useRef } from 'react'
import styled from 'styled-components'
import { OcrRegions } from './ocrRegions'
import { ScreenSpin } from 'admin/lib/ScreenSpin'
import { getRotation } from './common'

type ImageViewerProps = {
  src: string
  rotation: number
  ocrData: any
  ocrEnabled: boolean
  handleExtractionValueClicked: (event) => void
  ocrSearchText: string
  currentZoomLevel: number
  attachmentIndex: number
  hasMultipleAttachments?: boolean
}

export const ImageViewer = ({
  src,
  rotation = 0,
  ocrData,
  ocrEnabled,
  handleExtractionValueClicked,
  ocrSearchText = '',
  currentZoomLevel = 1,
  attachmentIndex = 1,
  hasMultipleAttachments = false
}: ImageViewerProps) => {
  const imageRef = useRef<HTMLImageElement>(new Image())

  const [width, setWidth] = useState(0)
  const [height, setHeight] = useState(0)
  const [originalWidth, setOriginalWidth] = useState(0)
  const [originalHeight, setOriginalHeight] = useState(0)

  function normalizeAngle(angle) {
    return ((angle % 360) + 360) % 360
  }

  const adjustedRotation = useMemo(() => {
    if (!ocrData) return rotation

    const { angle: ocrRotation } = ocrData
    const roundedOcrRotation = ocrRotation[attachmentIndex - 1]
      ? Math.round(ocrRotation[attachmentIndex - 1] / 90) * 90
      : 0

    return roundedOcrRotation !== undefined
      ? rotation - roundedOcrRotation
      : rotation
  }, [attachmentIndex, ocrData, rotation])

  const isRotated = [270, 90].includes(normalizeAngle(adjustedRotation))

  const onImgLoad = ({ target: img }) => {
    const { offsetHeight, offsetWidth } = img

    const container = document.querySelector('.Layouts__ContentContainer')
    const containerHeight = container?.clientHeight
    const startingHeight = containerHeight
      ? containerHeight - (hasMultipleAttachments ? 128 : 54)
      : 595

    setOriginalHeight(startingHeight)
    setOriginalWidth((startingHeight / offsetHeight) * offsetWidth)
  }

  useEffect(() => {
    if (originalWidth && originalHeight) {
      setWidth(originalWidth)
      setHeight(originalHeight)
    }
  }, [width, height, originalWidth, originalHeight])

  if (!imageRef.current) {
    return <ScreenSpin />
  }

  return (
    <Container
      isRotated={isRotated}
      imageWidth={width}
      imageHeight={height}
      currentZoomLevel={currentZoomLevel}
    >
      <RecordImage
        src={src}
        onLoad={onImgLoad}
        ref={imageRef}
        rotation={adjustedRotation}
        isRotated={isRotated}
        imageWidth={width}
        imageHeight={height}
        currentZoomLevel={currentZoomLevel}
      />
      {ocrData && ocrData.subRegions.length > 0 && (
        <OcrRegions
          type="image"
          ocrData={ocrData}
          rotation={adjustedRotation}
          isRotated={isRotated}
          ocrEnabled={ocrEnabled}
          handleExtractionValueClicked={handleExtractionValueClicked}
          pageNumber={1}
          attachmentIndex={attachmentIndex}
          width={width * currentZoomLevel}
          height={height * currentZoomLevel}
          ocrSearchText={ocrSearchText}
        ></OcrRegions>
      )}
    </Container>
  )
}

const Container = styled.div<{
  isRotated: boolean
  imageWidth: number
  imageHeight: number
  currentZoomLevel: number
}>`
  position: relative;
  width: ${({ isRotated, imageWidth, imageHeight, currentZoomLevel }) =>
    isRotated
      ? imageHeight * currentZoomLevel
      : imageWidth * currentZoomLevel}px;
  height: ${({ isRotated, imageWidth, imageHeight, currentZoomLevel }) =>
    isRotated
      ? imageWidth * currentZoomLevel
      : imageHeight * currentZoomLevel}px;
`

const RecordImage = styled.img<{
  rotation: number
  isRotated: boolean
  imageWidth: number
  imageHeight: number
  currentZoomLevel: number
}>`
  aspect-ratio: initial;
  transform: rotate(${({ rotation }) => getRotation(rotation)}deg);
  ${({ imageWidth, currentZoomLevel }) =>
    imageWidth ? `width: ${imageWidth * currentZoomLevel}px;` : ''}
  ${({ imageHeight, currentZoomLevel }) =>
    imageHeight ? `height: ${imageHeight * currentZoomLevel}px;` : ''}
  margin-top: ${({ isRotated, imageWidth, imageHeight, currentZoomLevel }) =>
    !isRotated
      ? '0'
      : `${((imageWidth - imageHeight) * currentZoomLevel) / 2}`}px;
  margin-left: ${({ isRotated, imageWidth, imageHeight, currentZoomLevel }) =>
    !isRotated
      ? '0'
      : `${((imageHeight - imageWidth) * currentZoomLevel) / 2}`}px;
`
